import type { PmeDemonstrativeRow } from '@/features/proposals/components/pme/proposal.mock';
import type { PmeResolvedCoverage } from '@/features/proposals/components/pme/utils/hydrate-proposal.util';
import {
  formatCurrencyBRL,
  parseCurrencyInput,
} from '@/features/quotes/utils/group-life/format';
import { resolveAverageRatePerMille } from '@/features/quotes/utils/group-life/average-rate.util';
import { firstMeaningfulValue } from '@/features/quotes/utils/group-life/coverage-unit-premium.util';
import { isServiceCoverage } from '@/features/quotes/utils/group-life/service-coverage.util';
import type { GroupLifeMemberCrudResponse } from '@/services/group-life';

const DASH = '—';

export type PmeAnalysisCoverageRow = {
  name: string;
  /** Detalhe sob o nome — nas diárias, "30 diárias × R$ 100,00". */
  detail?: string;
  totalInsuredCapital: string;
  averageRate: string;
  monthlyPremium: string;
};

export type PmeAnalysisServiceRow = {
  name: string;
  valuePerInsured: string;
  monthlyValue: string;
};

export type PmeAnalysisAgeCapitalRow = {
  age: string;
  capital: string;
  lives: string;
};

export type PmeAnalysisAfastadoRow = {
  name: string;
  sex: string;
  age: string;
  capital: string;
  cids: string[];
};

export type PmeAnalysisCriterionRow = {
  name: string;
  rules: string[];
  results: string[];
  /** Veredito por regra: true = passou (fica cinza mesmo em critério "Análise");
   *  null/ausente = segue o status do critério. */
  ruleOk?: Array<boolean | null>;
  /** Status POR REGRA: uma tag por linha, alinhada com `rules`/`results`.
   *  Ausente → a linha herda o status do critério. */
  lineStatus?: Array<'OK' | 'Análise'>;
  status: 'OK' | 'Análise';
};

/** Idade a partir de uma data `DD/MM/YYYY`. Retorna null se inválida. */
function computeAge(birthDate: string | undefined): number | null {
  if (!birthDate) return null;
  const parts = birthDate.split('/');
  if (parts.length !== 3) return null;
  const [day, month, year] = parts.map(Number);
  if (!day || !month || !year) return null;
  const birth = new Date(year, month - 1, day);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age >= 0 ? age : null;
}

function genderLabel(gender: string | undefined): string {
  if (gender === 'M') return 'Masculino';
  if (gender === 'F') return 'Feminino';
  return DASH;
}

/**
 * Coberturas e Serviços: nome + capital vêm das coberturas do plano resolvidas
 * pelo schema; o prêmio mensal vem do demonstrativo do cálculo (casado por
 * nome) e a taxa média vem da tarifação por cobertura.
 */
export function deriveCoverageRows(
  planCoverageGroups: { category: string; items: PmeResolvedCoverage[] }[],
  demonstrativeRows: PmeDemonstrativeRow[],
  resolveCoverageCapital?: (code: string, name: string) => number | undefined,
  /** Detalhe por código/nome (ver `dailyAllowanceDetails`). */
  detailByCoverage?: Map<string, string>
): PmeAnalysisCoverageRow[] {
  // `demonstrativeRows.total` já é o prêmio MENSAL da cobertura — o mesmo
  // número do Demonstrativo do resumo (`resolveMonthlyCoverageBreakdown`).
  const monthlyPremiumByName = new Map(
    demonstrativeRows.map(row => [row.description, row.total])
  );
  const rows: PmeAnalysisCoverageRow[] = [];
  for (const group of planCoverageGroups) {
    for (const item of group.items) {
      if (isServiceCoverage(item.code, item.name, item.group)) continue;

      // O capital do plano é o do GRUPO. No auxílio ele não existe — o
      // InsureMO não persiste capital de benefício —, e aí entra o resolvedor,
      // que traz o valor do catálogo já multiplicado pelas vidas.
      // `firstMeaningfulValue` e não `??`: o campo ausente chega ZERO, não
      // nulo, e um `??` pararia no zero deixando a linha em R$ 0,00.
      const planCapital = parseCurrencyInput(item.capital ?? '');
      const capitalValue = firstMeaningfulValue(
        planCapital,
        resolveCoverageCapital?.(item.code, item.name)
      );
      const monthlyPremium = parseCurrencyInput(
        monthlyPremiumByName.get(item.name) ?? ''
      );

      const detail =
        detailByCoverage?.get(item.code) ?? detailByCoverage?.get(item.name);
      rows.push({
        name: item.name,
        ...(detail ? { detail } : {}),
        totalInsuredCapital:
          capitalValue == null ? DASH : formatCurrencyBRL(capitalValue),
        // Taxa derivada da própria linha: prêmio ÷ capital × 1000 — sobre o
        // MESMO capital que a coluna exibe, para o auxílio também ter taxa.
        averageRate: resolveAverageRatePerMille(monthlyPremium, capitalValue),
        monthlyPremium:
          monthlyPremium == null ? DASH : formatCurrencyBRL(monthlyPremium),
      });
    }
  }
  return rows;
}

/**
 * Serviços e assistências selecionados (inclusive capitalização).
 *
 * "Valor Mensal" é o `total` MENSAL do demonstrativo (com IOF), como nas
 * coberturas; "Valor por Segurado" é esse mensal ÷ `livesCount`, as vidas da
 * cotação. Sem linha no demonstrativo, ou sem a contagem de vidas ainda
 * carregada, a coluna fica com o traço.
 */
export function deriveServiceRows(
  planCoverageGroups: { category: string; items: PmeResolvedCoverage[] }[],
  demonstrativeRows: PmeDemonstrativeRow[],
  livesCount: number
): PmeAnalysisServiceRow[] {
  const monthlyTotalByName = new Map(
    demonstrativeRows.map(row => [row.description, row.total])
  );
  return planCoverageGroups.flatMap(group =>
    group.items
      .filter(item => isServiceCoverage(item.code, item.name, item.group))
      .map(coverage => {
        const monthlyValue = parseCurrencyInput(
          monthlyTotalByName.get(coverage.name) ?? ''
        );
        const unitValue =
          monthlyValue != null && livesCount > 0
            ? monthlyValue / livesCount
            : null;
        return {
          name: coverage.name,
          valuePerInsured:
            unitValue == null ? DASH : formatCurrencyBRL(unitValue),
          monthlyValue:
            monthlyValue == null ? DASH : formatCurrencyBRL(monthlyValue),
        };
      })
  );
}

/** Capital exibido de uma vida — por padrão, o que veio de `members`. */
const memberCapital = (member: GroupLifeMemberCrudResponse): string =>
  member.capital;

type ResolveMemberCapital = typeof memberCapital;

/**
 * Idade / Capital: agrupa as vidas por idade, somando capital e contando vidas.
 * `resolveCapital` permite trocar o capital de `members` (em Múltiplo Salarial
 * é o salário) pelo tarifado.
 */
export function deriveAgeCapitalRows(
  members: GroupLifeMemberCrudResponse[],
  resolveCapital: ResolveMemberCapital = memberCapital
): PmeAnalysisAgeCapitalRow[] {
  const byAge = new Map<number, { capital: number; lives: number }>();
  for (const member of members) {
    const age = computeAge(member.dateOfBirth);
    if (age == null) continue;
    const current = byAge.get(age) ?? { capital: 0, lives: 0 };
    current.capital += parseCurrencyInput(resolveCapital(member) ?? '') ?? 0;
    current.lives += 1;
    byAge.set(age, current);
  }
  return [...byAge.entries()]
    .sort((a, b) => a[0] - b[0])
    .map(([age, agg]) => ({
      age: String(age),
      capital: formatCurrencyBRL(agg.capital),
      lives: String(agg.lives),
    }));
}

/** Afastados: vidas marcadas como `awayFromWork`. */
export function deriveAfastadoRows(
  members: GroupLifeMemberCrudResponse[],
  resolveCapital: ResolveMemberCapital = memberCapital
): PmeAnalysisAfastadoRow[] {
  return members
    .filter(member => member.awayFromWork)
    .map(member => {
      const age = computeAge(member.dateOfBirth);
      const cids = (member.cids ?? []).filter(Boolean);
      const capital = resolveCapital(member);
      return {
        name: member.name || DASH,
        sex: genderLabel(member.gender),
        age: age != null ? String(age) : DASH,
        capital: capital
          ? formatCurrencyBRL(parseCurrencyInput(capital) ?? 0)
          : DASH,
        cids,
      };
    });
}

// Critérios de Aceitação: a derivação vive em
// `@/features/analyses/pme/acceptance-criteria` (catálogo por messageCode,
// compartilhado com a página de análise de cotação).
